import type { Action, ValidationResult } from "../../types/actions";
import type { Project } from "../../types/project";
import type { Marker } from "../../types";
import type { MutableTimeline } from "../../utils/immutable-updates";
import { createInverseAction, registerActionHandler } from "../registry";
import type { ActionHandler, ActionHandlerContext } from "../registry";

const ok = (): ValidationResult => ({ valid: true, errors: [] });
const err = (message: string, path?: string): ValidationResult => ({
  valid: false,
  errors: [{ code: "INVALID_PARAMS", message, path }],
});

function markers(project: Project): readonly Marker[] {
  return project.timeline.markers;
}

function setMarkers(project: Project, next: Marker[]): void {
  (project.timeline as MutableTimeline).markers = next;
}

function requireExistingMarker(
  action: Action,
  project: Project,
): ValidationResult {
  const markerId = (action.params as { markerId?: unknown }).markerId;
  if (typeof markerId !== "string" || !markerId) {
    return err("Marker ID is required and must be a string", "params.markerId");
  }
  return markers(project).some((m) => m.id === markerId)
    ? ok()
    : err(`Marker with ID ${markerId} not found`, "params.markerId");
}

const markerAdd: ActionHandler = {
  type: "marker/add",
  validate(action: Action): ValidationResult {
    const time = (action.params as { time?: unknown }).time;
    return typeof time === "number" && time >= 0
      ? ok()
      : err("Marker time must be a non-negative number", "params.time");
  },
  apply(action: Action, project: Project, ctx: ActionHandlerContext): void {
    const params = action.params as { time: number; label: string; color: string };
    // Was `marker-${Date.now()}-${random}`, which collides for two markers
    // added inside one millisecond and leaves the second one unaddressable by
    // its own inverse. Same defect the effect ids had.
    const marker: Marker = {
      id: `marker-${crypto.randomUUID()}`,
      time: params.time,
      label: params.label,
      color: params.color,
    };
    setMarkers(project, [...markers(project), marker]);
    ctx.lastAddedIds.set("marker", marker.id);
  },
  invert(action: Action): Action | null {
    return createInverseAction(action, "marker/remove", {
      markerId: "__LAST_ADDED__",
    });
  },
};

/**
 * The inverse of a remove, so it carries the marker and the index it sat at.
 * It had no validate and no invert case of its own in the legacy switches: it
 * is only ever minted by this file, never by a caller.
 */
const markerRestore: ActionHandler = {
  type: "marker/restore",
  validate(action: Action): ValidationResult {
    const marker = (action.params as { marker?: Marker }).marker;
    return marker && typeof marker.id === "string"
      ? ok()
      : err("marker/restore requires a marker with an id", "params.marker");
  },
  apply(action: Action, project: Project): void {
    const params = action.params as { marker: Marker; position: number };
    const next = [...markers(project)];
    const position = Math.min(Math.max(params.position, 0), next.length);
    next.splice(position, 0, { ...params.marker });
    setMarkers(project, next);
  },
  invert(action: Action): Action | null {
    const marker = (action.params as { marker: Marker }).marker;
    return createInverseAction(action, "marker/remove", { markerId: marker.id });
  },
};

const markerRemove: ActionHandler = {
  type: "marker/remove",
  validate: requireExistingMarker,
  apply(action: Action, project: Project): void {
    const markerId = (action.params as { markerId: string }).markerId;
    setMarkers(
      project,
      markers(project).filter((m) => m.id !== markerId),
    );
  },
  invert(action: Action, projectBefore: Project): Action | null {
    const markerId = (action.params as { markerId: string }).markerId;
    const position = markers(projectBefore).findIndex((m) => m.id === markerId);
    if (position === -1) return null;
    return createInverseAction(action, "marker/restore", {
      marker: { ...markers(projectBefore)[position] },
      position,
    });
  },
};

const markerUpdate: ActionHandler = {
  type: "marker/update",
  validate: requireExistingMarker,
  apply(action: Action, project: Project): void {
    const params = action.params as { markerId: string; updates: Partial<Marker> };
    setMarkers(
      project,
      markers(project).map((m) =>
        m.id === params.markerId ? { ...m, ...params.updates } : m,
      ),
    );
  },
  invert(action: Action, projectBefore: Project): Action | null {
    const markerId = (action.params as { markerId: string }).markerId;
    const prior = markers(projectBefore).find((m) => m.id === markerId);
    if (!prior) return null;
    return createInverseAction(action, "marker/update", {
      markerId,
      updates: { time: prior.time, label: prior.label, color: prior.color },
    });
  },
};

for (const handler of [markerAdd, markerRestore, markerRemove, markerUpdate]) {
  registerActionHandler(handler);
}
